import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  IconButton,
  CommandBar,
  ICommandBarItemProps,
  Pivot,
  PivotItem,
  ProgressIndicator,
  DetailsList,
  IColumn,
  SelectionMode,
  Panel,
  PanelType
} from '@fluentui/react';
import {
  DatabaseRegular,
  ConnectorRegular,
  DocumentRegular,
  PlayRegular,
  PauseRegular,
  StopRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  ClockRegular,
  WarningRegular,
  ChartMultipleRegular,
  EyeRegular,
  ArrowClockwiseRegular,
  DeleteRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './ConnectorHubDashboard.module.css';

interface DashboardStats {
  totalIndexes: number;
  activeIndexes: number;
  totalJobs: number;
  runningJobs: number;
  completedJobs: number;
  failedJobs: number;
  totalDocuments: number;
  documentsProcessedToday: number;
  avgCrawlTime: number;
  systemHealth: 'healthy' | 'warning' | 'error';
}

interface RunningJob {
  id: string;
  name: string;
  connectorType: string;
  indexName: string;
  status: 'running' | 'queued' | 'paused';
  progress: number;
  startTime: string;
  estimatedCompletion: string;
  itemsProcessed: number;
  itemsTotal: number;
  currentActivity: string;
  documentsPerMinute: number;
  errorsCount: number;
}

interface IndexStatistics {
  id: string;
  name: string;
  documentCount: number;
  sizeGB: number;
  lastUpdated: string;
  status: 'active' | 'inactive' | 'indexing' | 'error';
  queryCount24h: number;
  avgResponseTime: number;
  connectorType: string;
}

interface RecentActivity {
  id: string;
  timestamp: string;
  type: 'job_started' | 'job_completed' | 'job_failed' | 'index_created' | 'system_alert';
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  jobId?: string;
  indexId?: string;
}

const ConnectorHubDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [runningJobs, setRunningJobs] = useState<RunningJob[]>([]);
  const [indexStats, setIndexStats] = useState<IndexStatistics[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [selectedJob, setSelectedJob] = useState<RunningJob | null>(null);
  const [showJobPanel, setShowJobPanel] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);

  useEffect(() => {
    loadDashboardData();
    
    // Auto-refresh every 30 seconds if enabled
    const interval = autoRefresh ? setInterval(loadDashboardData, 30000) : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const clearIndex = async (indexId: string, indexName: string) => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to clear all data from index "${indexName}"? This action cannot be undone.`
      );
      
      if (!confirmed) return;

      setMessage({ text: `Clearing index "${indexName}"...`, type: MessageBarType.warning });

      const response = await authenticatedFetch(`/api/connector-hub/indexes/${indexId}/clear`, {
        method: 'POST'
      });

      const result = await response.json();

      if (result.success) {
        setMessage({ 
          text: `Index "${indexName}" cleared successfully. ${result.documentsRemoved || 0} documents removed.`, 
          type: MessageBarType.success 
        });
        
        // Refresh data to show updated counts
        await loadDashboardData();
      } else {
        // Handle specific errors
        if (result.error?.includes('Azure Search not available')) {
          setMessage({ 
            text: `Clear index operation requires Azure Search configuration. Index "${indexName}" clear request was received but Azure Search is not available.`, 
            type: MessageBarType.warning 
          });
        } else {
          setMessage({ 
            text: `Failed to clear index "${indexName}": ${result.error}`, 
            type: MessageBarType.error 
          });
        }
      }
    } catch (error) {
      console.error('Error clearing index:', error);
      setMessage({ 
        text: `Error clearing index "${indexName}": ${error instanceof Error ? error.message : 'Unknown error'}`, 
        type: MessageBarType.error 
      });
    }

    // Clear message after 8 seconds for longer messages
    setTimeout(() => setMessage(null), 8000);
  };

  const loadDashboardData = async () => {
    try {
      setRefreshing(true);
      
      // Load data with proper error handling for each endpoint
      let statsResult = { success: false, data: null };
      let jobsResult = { success: false, data: [] };
      let indexesResult = { success: false, indexes: [] };
      let activityResult = { success: false, data: [] };

      try {
        const statsResponse = await authenticatedFetch('/api/connector-hub/dashboard/stats');
        if (statsResponse.ok) {
          statsResult = await statsResponse.json();
        }
      } catch (error) {
        console.warn('Failed to load dashboard stats:', error);
      }

      try {
        const jobsResponse = await authenticatedFetch('/api/connector-hub/dashboard/running-jobs');
        if (jobsResponse.ok) {
          jobsResult = await jobsResponse.json();
        }
      } catch (error) {
        console.warn('Failed to load running jobs:', error);
      }

      try {
        const indexesResponse = await authenticatedFetch('/api/connector-hub/indexes');
        if (indexesResponse.ok) {
          indexesResult = await indexesResponse.json();
        }
      } catch (error) {
        console.warn('Failed to load indexes:', error);
      }

      try {
        const activityResponse = await authenticatedFetch('/api/connector-hub/dashboard/recent-activity');
        if (activityResponse.ok) {
          activityResult = await activityResponse.json();
        }
      } catch (error) {
        console.warn('Failed to load recent activity:', error);
      }

      // Set stats data with fallback
      if (statsResult.success && statsResult.data) {
        setDashboardStats(statsResult.data);
      } else {
        setDashboardStats({
          totalIndexes: 0,
          activeIndexes: 0,
          totalJobs: 0,
          runningJobs: 0,
          completedJobs: 0,
          failedJobs: 0,
          totalDocuments: 0,
          documentsProcessedToday: 0,
          avgCrawlTime: 0,
          systemHealth: 'healthy'
        });
      }

      // Set running jobs with proper validation
      if (jobsResult.success && Array.isArray(jobsResult.data)) {
        setRunningJobs(jobsResult.data);
      } else {
        setRunningJobs([]);
      }
      
      // Handle index stats with proper validation
      if (indexesResult.success && Array.isArray(indexesResult.indexes)) {
        const formattedIndexStats = indexesResult.indexes.map((index: any) => ({
          id: index.id || index.name || 'unknown',
          name: index.name || 'Unknown Index',
          documentCount: index.documentCount || 0,
          sizeGB: index.sizeGB || 0,
          lastUpdated: index.lastUpdated || new Date().toISOString(),
          status: index.status || 'unknown',
          queryCount24h: index.queryCount24h || 0,
          avgResponseTime: index.avgResponseTime || 0,
          connectorType: index.connectorType || 'unknown'
        }));
        setIndexStats(formattedIndexStats);
      } else {
        setIndexStats([]);
      }
      
      // Set recent activity with proper validation
      if (activityResult.success && Array.isArray(activityResult.data)) {
        setRecentActivity(activityResult.data);
      } else {
        setRecentActivity([]);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set empty data with proper structure
      setDashboardStats({
        totalIndexes: 0,
        activeIndexes: 0,
        totalJobs: 0,
        runningJobs: 0,
        completedJobs: 0,
        failedJobs: 0,
        totalDocuments: 0,
        documentsProcessedToday: 0,
        avgCrawlTime: 0,
        systemHealth: 'healthy'
      });
      
      setRunningJobs([]);
      setIndexStats([]);
      setRecentActivity([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const renderStatsCards = () => {
    if (!dashboardStats) return null;

    const statsCards = [
      {
        title: 'Search Indexes',
        value: dashboardStats.totalIndexes || 0,
        subtitle: `${dashboardStats.activeIndexes || 0} active`,
        icon: <DatabaseRegular style={{ fontSize: 32, color: '#0078d4' }} />,
        trend: '+2 this week'
      },
      {
        title: 'Running Jobs',
        value: dashboardStats.runningJobs || 0,
        subtitle: `${dashboardStats.totalJobs || 0} total`,
        icon: <ConnectorRegular style={{ fontSize: 32, color: '#107c10' }} />,
        trend: '3 in progress'
      },
      {
        title: 'Total Documents',
        value: (dashboardStats.totalDocuments || 0).toLocaleString(),
        subtitle: `+${(dashboardStats.documentsProcessedToday || 0).toLocaleString()} today`,
        icon: <DocumentRegular style={{ fontSize: 32, color: '#d83b01' }} />,
        trend: '+2.4% growth'
      },
      {
        title: 'System Health',
        value: dashboardStats.systemHealth === 'healthy' ? '✓' : dashboardStats.systemHealth === 'warning' ? '⚠' : '!',
        subtitle: dashboardStats.systemHealth === 'healthy' ? 'All systems operational' : 
                 dashboardStats.systemHealth === 'warning' ? 'Minor issues detected' :
                 dashboardStats.systemHealth === 'error' ? 'Issues require attention' : 'Status unknown',
        icon: <ChartMultipleRegular style={{ 
          fontSize: 32, 
          color: dashboardStats.systemHealth === 'healthy' ? '#107c10' : 
                dashboardStats.systemHealth === 'warning' ? '#ff8c00' : '#d83b01' 
        }} />,
        trend: dashboardStats.avgCrawlTime > 0 ? `Avg: ${dashboardStats.avgCrawlTime}min` : 'No recent activity'
      }
    ];

    return (
      <div className={styles.statsGrid}>
        {statsCards.map((card, index) => (
          <div key={index} className={styles.statCard}>
            <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
              {card.icon}
              <Stack tokens={{ childrenGap: 4 }} style={{ flex: 1 }}>
                <Text variant="medium" style={{ color: '#666' }}>{card.title}</Text>
                <Text variant="xxLarge" style={{ fontWeight: 700, color: '#1a365d' }}>
                  {card.value}
                </Text>
                <Text variant="small" style={{ color: '#666' }}>{card.subtitle}</Text>
                <Text variant="small" style={{ color: '#107c10', fontWeight: 500 }}>
                  {card.trend}
                </Text>
              </Stack>
            </Stack>
          </div>
        ))}
      </div>
    );
  };

  const renderRunningJobs = () => {
    const jobColumns: IColumn[] = [
      {
        key: 'name',
        name: 'Job Name',
        fieldName: 'name',
        minWidth: 200,
        maxWidth: 250,
        onRender: (item: RunningJob) => (
          <Stack>
            <Text variant="medium" style={{ fontWeight: 500 }}>{item.name}</Text>
            <Text variant="small" style={{ color: '#666' }}>
              {item.connectorType} → {item.indexName}
            </Text>
          </Stack>
        )
      },
      {
        key: 'progress',
        name: 'Progress',
        minWidth: 200,
        maxWidth: 250,
        onRender: (item: RunningJob) => (
          <Stack tokens={{ childrenGap: 4 }}>
            <ProgressIndicator
              percentComplete={item.progress / 100}
              barHeight={6}
            />
            <Stack horizontal horizontalAlign="space-between">
              <Text variant="small">{item.progress || 0}%</Text>
              <Text variant="small">
                {(item.itemsProcessed || 0).toLocaleString()} / {(item.itemsTotal || 0).toLocaleString()}
              </Text>
            </Stack>
          </Stack>
        )
      },
      {
        key: 'performance',
        name: 'Performance',
        minWidth: 120,
        maxWidth: 150,
        onRender: (item: RunningJob) => (
          <Stack tokens={{ childrenGap: 2 }}>
            <Text variant="small">{item.documentsPerMinute}/min</Text>
            <Text variant="small" style={{ color: item.errorsCount > 0 ? '#d83b01' : '#107c10' }}>
              {item.errorsCount} errors
            </Text>
          </Stack>
        )
      },
      {
        key: 'status',
        name: 'Status',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: RunningJob) => (
          <div className={`${styles.statusBadge} ${styles[item.status]}`}>
            {item.status === 'running' && <PlayRegular style={{ fontSize: 12 }} />}
            {item.status === 'paused' && <PauseRegular style={{ fontSize: 12 }} />}
            {item.status === 'queued' && <ClockRegular style={{ fontSize: 12 }} />}
            <span style={{ marginLeft: '4px' }}>{item.status}</span>
          </div>
        )
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 100,
        onRender: (item: RunningJob) => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <IconButton
              iconProps={{ iconName: 'View' }}
              title="View Details"
              onClick={() => {
                setSelectedJob(item);
                setShowJobPanel(true);
              }}
            />
            <IconButton
              iconProps={{ iconName: item.status === 'running' ? 'Pause' : 'Play' }}
              title={item.status === 'running' ? 'Pause Job' : 'Resume Job'}
            />
            <IconButton
              iconProps={{ iconName: 'Stop' }}
              title="Stop Job"
            />
          </Stack>
        )
      }
    ];

    return (
      <Stack tokens={{ childrenGap: 16 }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant="large" style={{ fontWeight: 600 }}>Running Jobs</Text>
          <Text variant="medium" style={{ color: '#666' }}>
            {Array.isArray(runningJobs) ? runningJobs.length : 0} active
          </Text>
        </Stack>
        
        {Array.isArray(runningJobs) && runningJobs.length > 0 ? (
          <DetailsList
            items={runningJobs}
            columns={jobColumns}
            selectionMode={SelectionMode.none}
            className={styles.jobsList}
          />
        ) : (
          <div className={styles.emptyState}>
            <ConnectorRegular style={{ fontSize: 48, color: '#d1d5db' }} />
            <Text variant="large" style={{ fontWeight: 600, color: '#374151' }}>
              No Running Jobs
            </Text>
            <Text style={{ color: '#6b7280' }}>
              All connector jobs are currently idle
            </Text>
          </div>
        )}
      </Stack>
    );
  };

  const renderIndexStatistics = () => {
    const indexColumns: IColumn[] = [
      {
        key: 'name',
        name: 'Index Name',
        fieldName: 'name',
        minWidth: 200,
        maxWidth: 250,
        onRender: (item: IndexStatistics) => (
          <Stack>
            <Text variant="medium" style={{ fontWeight: 500 }}>{item.name}</Text>
            <Text variant="small" style={{ color: '#666' }}>
              {item.connectorType}
            </Text>
          </Stack>
        )
      },
      {
        key: 'documents',
        name: 'Documents',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: IndexStatistics) => (
          <Stack tokens={{ childrenGap: 2 }}>
            <Text variant="medium" style={{ fontWeight: 500 }}>
              {(item.documentCount || 0).toLocaleString()}
            </Text>
            <Text variant="small" style={{ color: '#666' }}>
              {item.sizeGB || 0} GB
            </Text>
          </Stack>
        )
      },
      {
        key: 'queries',
        name: 'Queries (24h)',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: IndexStatistics) => (
          <Stack tokens={{ childrenGap: 2 }}>
            <Text variant="medium" style={{ fontWeight: 500 }}>
              {(item.queryCount24h || 0).toLocaleString()}
            </Text>
            <Text variant="small" style={{ color: '#666' }}>
              {item.avgResponseTime || 0}ms avg
            </Text>
          </Stack>
        )
      },
      {
        key: 'status',
        name: 'Status',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: IndexStatistics) => (
          <div className={`${styles.statusBadge} ${styles[item.status]}`}>
            {item.status}
          </div>
        )
      },
      {
        key: 'lastUpdated',
        name: 'Last Updated',
        minWidth: 150,
        onRender: (item: IndexStatistics) => (
          <Text variant="small">
            {new Date(item.lastUpdated).toLocaleString()}
          </Text>
        )
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 120,
        onRender: (item: IndexStatistics) => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <IconButton
              iconProps={{ iconName: 'View' }}
              title="View Index Details"
              onClick={() => {
                // TODO: Implement view index details
                console.log('View index details:', item.id);
              }}
            />
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Clear Index Data"
              onClick={() => clearIndex(item.id, item.name)}
            />
          </Stack>
        )
      }
    ];

    return (
      <Stack tokens={{ childrenGap: 16 }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant="large" style={{ fontWeight: 600 }}>Index Statistics</Text>
          <Text variant="medium" style={{ color: '#666' }}>
            {Array.isArray(indexStats) ? indexStats.length : 0} indexes
          </Text>
        </Stack>
        
        {Array.isArray(indexStats) && indexStats.length > 0 ? (
          <DetailsList
            items={indexStats}
            columns={indexColumns}
            selectionMode={SelectionMode.none}
            className={styles.indexList}
          />
        ) : (
          <div className={styles.emptyState}>
            <DatabaseRegular style={{ fontSize: 48, color: '#d1d5db' }} />
            <Text variant="large" style={{ fontWeight: 600, color: '#374151' }}>
              No Indexes Found
            </Text>
            <Text style={{ color: '#6b7280' }}>
              Create an index to get started with document search
            </Text>
          </div>
        )}
      </Stack>
    );
  };

  const renderRecentActivity = () => {
    const getActivityIcon = (type: string, severity: string) => {
      switch (type) {
        case 'job_started':
          return <PlayRegular style={{ fontSize: 16, color: '#0078d4' }} />;
        case 'job_completed':
          return <CheckmarkCircleRegular style={{ fontSize: 16, color: '#107c10' }} />;
        case 'job_failed':
          return <ErrorCircleRegular style={{ fontSize: 16, color: '#d83b01' }} />;
        case 'system_alert':
          return <WarningRegular style={{ fontSize: 16, color: '#ff8c00' }} />;
        default:
          return <DocumentRegular style={{ fontSize: 16, color: '#666' }} />;
      }
    };

    return (
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="large" style={{ fontWeight: 600 }}>Recent Activity</Text>
        
        <div className={styles.activityList}>
          {Array.isArray(recentActivity) && recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <div key={activity.id || index} className={styles.activityItem}>
                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                  <div className={styles.activityIcon}>
                    {getActivityIcon(activity.type || 'unknown', activity.severity || 'info')}
                  </div>
                  <Stack tokens={{ childrenGap: 2 }} style={{ flex: 1 }}>
                    <Text variant="medium" style={{ fontWeight: 500 }}>
                      {activity.message || 'No message'}
                    </Text>
                    <Text variant="small" style={{ color: '#666' }}>
                      {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'N/A'}
                    </Text>
                  </Stack>
                  {activity.jobId && (
                    <IconButton
                      iconProps={{ iconName: 'View' }}
                      title="View Job"
                      styles={{ root: { minWidth: '32px' } }}
                    />
                  )}
                </Stack>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <DocumentRegular style={{ fontSize: 48, color: '#d1d5db' }} />
              <Text variant="large" style={{ fontWeight: 600, color: '#374151' }}>
                No Recent Activity
              </Text>
              <Text style={{ color: '#6b7280' }}>
                Activity will appear here when jobs are running
              </Text>
            </div>
          )}
        </div>
      </Stack>
    );
  };

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'refresh',
      text: 'Refresh',
      iconProps: { iconName: 'Refresh' },
      onClick: () => {
        loadDashboardData();
      },
      disabled: refreshing
    },
    {
      key: 'autoRefresh',
      text: autoRefresh ? 'Disable Auto-refresh' : 'Enable Auto-refresh',
      iconProps: { iconName: autoRefresh ? 'Pause' : 'Play' },
      onClick: () => {
        setAutoRefresh(!autoRefresh);
      }
    }
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size={SpinnerSize.large} label="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <Stack tokens={{ childrenGap: 24 }}>
        {/* Message Bar */}
        {message && (
          <MessageBar
            messageBarType={message.type}
            onDismiss={() => setMessage(null)}
            dismissButtonAriaLabel="Close"
          >
            {message.text}
          </MessageBar>
        )}

        {/* Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 8 }}>
            <Text variant="xxLarge" style={{ fontWeight: 700, color: '#1a365d' }}>
              Connector Hub Dashboard
            </Text>
            <Text variant="medium" style={{ color: '#64748b' }}>
              Monitor and manage your data connector operations
            </Text>
          </Stack>
          <CommandBar items={commandBarItems} />
        </Stack>

        {/* Stats Cards */}
        {renderStatsCards()}

        {/* Main Content Tabs */}
        <Pivot defaultSelectedKey="jobs">
          <PivotItem headerText="Running Jobs" itemKey="jobs" itemIcon="Play">
            {renderRunningJobs()}
          </PivotItem>
          
          <PivotItem headerText="Index Statistics" itemKey="indexes" itemIcon="Database">
            {renderIndexStatistics()}
          </PivotItem>
          
          <PivotItem headerText="Recent Activity" itemKey="activity" itemIcon="Activity">
            {renderRecentActivity()}
          </PivotItem>
        </Pivot>

        {/* Job Details Panel */}
        <Panel
          isOpen={showJobPanel}
          onDismiss={() => setShowJobPanel(false)}
          headerText={selectedJob?.name || 'Job Details'}
          type={PanelType.medium}
        >
          {selectedJob && (
            <Stack tokens={{ childrenGap: 20 }}>
              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="medium" style={{ fontWeight: 600 }}>Current Activity</Text>
                <Text variant="small" style={{ fontFamily: 'monospace', background: '#f8f9fa', padding: '8px', borderRadius: '4px' }}>
                  {selectedJob.currentActivity}
                </Text>
              </Stack>

              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="medium" style={{ fontWeight: 600 }}>Performance Metrics</Text>
                <Stack tokens={{ childrenGap: 8 }}>
                  <Text variant="small">
                    <strong>Processing Rate:</strong> {selectedJob.documentsPerMinute || 0} documents/minute
                  </Text>
                  <Text variant="small">
                    <strong>Started:</strong> {selectedJob.startTime ? new Date(selectedJob.startTime).toLocaleString() : 'N/A'}
                  </Text>
                  <Text variant="small">
                    <strong>Estimated Completion:</strong> {selectedJob.estimatedCompletion ? new Date(selectedJob.estimatedCompletion).toLocaleString() : 'N/A'}
                  </Text>
                  <Text variant="small" style={{ color: (selectedJob.errorsCount || 0) > 0 ? '#d83b01' : '#107c10' }}>
                    <strong>Errors:</strong> {selectedJob.errorsCount || 0}
                  </Text>
                </Stack>
              </Stack>

              <ProgressIndicator
                label="Overall Progress"
                description={`${(selectedJob.itemsProcessed || 0).toLocaleString()} of ${(selectedJob.itemsTotal || 0).toLocaleString()} items processed`}
                percentComplete={(selectedJob.progress || 0) / 100}
              />
            </Stack>
          )}
        </Panel>
      </Stack>
    </div>
  );
};

export default ConnectorHubDashboard;
